import { subscriptionQuery, useCancelSubscriptionMutation } from "@/api/subscriptions";
import { PageInset } from "@/components/PageInset";
import { SubscriptionDialog } from "@/components/SubscriptionDialog";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { RECEIPT_DIALOG_EXIT_DURATION } from "@/components/ui/ReceiptDialog";
import { Textarea } from "@/components/ui/Textarea";
import { formatDate } from "@/utils/date";
import { getCancellationPreview, joinWithAnd } from "@/utils/subscriptions";
import { splitTitleCase } from "@common/utils/string";
import { Box, Field, Flex, Img, QueryHandler, Text, UnstyledButton } from "@givenwell/components";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon } from "@givenwell/core-solid-icons";
import { SubscriptionDetails } from "@givenwell/marketplace-api";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { Navigate, createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_auth/_app/purchases/subscriptions/$subscriptionId/cancel")({
  component: Page,
});

const route = getRouteApi("/_auth/_app/purchases/subscriptions/$subscriptionId/cancel");

function Page() {
  const { subscriptionId } = route.useParams();
  const subscription = useQuery(subscriptionQuery(subscriptionId));

  const [step, setStep] = useState<"confirm" | "start" | "reason">("start");
  const [reason, setReason] = useState("");

  return (
    <PageWrapper css={{ pb: 0 }}>
      <QueryHandler
        query={subscription}
        error={() => <Navigate to="/purchases" replace />}
        empty={() => <Navigate to="/purchases" replace />}
        success={({ data: subscription }) => {
          if (step === "start") {
            return (
              <StartCancelStep
                subscription={subscription}
                onContinue={reason => {
                  setReason(reason);
                  if (reason) {
                    setStep("confirm");
                  } else {
                    setStep("reason");
                  }
                }}
              />
            );
          }
          if (step === "reason") {
            return (
              <ReasonStep
                onBack={() => setStep("start")}
                onContinue={reason => {
                  setReason(reason);
                  setStep("confirm");
                }}
              />
            );
          }
          if (step === "confirm") {
            return <ConfirmCancelStep subscription={subscription} reason={reason} onBack={() => setStep("start")} />;
          }
          return (
            <StartCancelStep
              subscription={subscription}
              onContinue={reason => {
                setReason(reason);
                if (reason) {
                  setStep("confirm");
                } else {
                  setStep("reason");
                }
              }}
            />
          );
        }}
      />
    </PageWrapper>
  );
}

const REASONS = [
  { label: "My schedule has changed", value: "My schedule has changed" },
  { label: "I’ve changed my mind", value: "I’ve changed my mind" },
  {
    label: "Supplier has asked me to cancel",
    value: "Supplier has asked me to cancel",
  },
  { label: "Other reason", value: "" },
];

function StartCancelStep({
  subscription,
  onContinue,
}: {
  subscription: SubscriptionDetails;
  onContinue: (reason: string) => void;
}) {
  const navigate = useNavigate();

  if (subscription.status === "cancellation_pending" || subscription.status === "cancelled") {
    return <div>This subscription has already been cancelled</div>;
  }

  return (
    <>
      <Flex css={{ h: 64, items: "center", px: 8 }}>
        <IconButton
          aria-label="Go back"
          variant="plain"
          onClick={() =>
            navigate({
              to: "/purchases",
            })
          }
        >
          <ChevronLeftIcon />
        </IconButton>
        <Box css={{ flex: 1 }} />
        <Text css={{ weight: 500 }}>Cancel subscription</Text>
        <Box css={{ flex: 1 }} />
        <IconButton
          aria-label="Close"
          variant="plain"
          onClick={() =>
            navigate({
              to: "/purchases",
            })
          }
        >
          <CloseIcon />
        </IconButton>
      </Flex>
      <PageInset css={{ gap: 16, flexGrow: 1, pb: 16 }}>
        <Text css={{ fontScale: "2xl", fontWeight: 700 }}>Reason for cancelling</Text>
        <div>
          {REASONS.map(reason => (
            <UnstyledButton
              key={reason.value}
              css={{
                w: "100%",
                h: 60,
                d: "flex",
                items: "center",
                justify: "space-between",
                borderTop: "1px solid",
                borderColor: colors.outline,
                color: colors.blue500,
              }}
              onClick={() => onContinue(reason.value)}
            >
              {reason.label}
              <ChevronRightIcon />
            </UnstyledButton>
          ))}
        </div>
      </PageInset>
    </>
  );
}

function ReasonStep({ onBack, onContinue }: { onBack: () => void; onContinue: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  const navigate = useNavigate();
  return (
    <>
      <Flex css={{ h: 64, items: "center", px: 8 }}>
        <IconButton aria-label="Go back" variant="plain" onClick={onBack}>
          <ChevronLeftIcon />
        </IconButton>
        <Box css={{ flex: 1 }} />
        <Text css={{ weight: 500 }}>Cancel subscription</Text>
        <Box css={{ flex: 1 }} />
        <IconButton aria-label="Close" variant="plain" onClick={() => navigate({ to: "/purchases" })}>
          <CloseIcon />
        </IconButton>
      </Flex>
      <PageInset css={{ gap: 16, flexGrow: 1, pb: 16 }}>
        <Text css={{ fontScale: "2xl", fontWeight: 700 }}>Please tell us more</Text>
        <Text>
          Please tell us why you’ve decided to cancel this subscription. Your feedback won’t be shared with the
          supplier.
        </Text>
        <form
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
          onSubmit={e => {
            e.preventDefault();
            onContinue(reason);
          }}
        >
          <Field>
            <Textarea placeholder="Your feedback helps us improve" value={reason} onValueChange={setReason} />
          </Field>
          <Box css={{ flex: 1 }} />
          <Button variant="tonal" color="primary" size="lg" disabled={!reason} type="submit">
            Continue
          </Button>
        </form>
      </PageInset>
    </>
  );
}

function ConfirmCancelStep({
  subscription,
  onBack,
  reason,
}: {
  subscription: SubscriptionDetails;
  reason: string;
  onBack: () => void;
}) {
  const mut = useCancelSubscriptionMutation();
  const navigate = useNavigate();
  const [completed, setCompleted] = useState(false);
  const preview = getCancellationPreview(subscription);
  const remaining = preview.remainingPaymentDates;
  return (
    <>
      <Flex css={{ h: 64, items: "center", px: 8 }}>
        <IconButton aria-label="Go back" variant="plain" onClick={onBack}>
          <ChevronLeftIcon />
        </IconButton>
        <Box css={{ flex: 1 }} />
        <Text css={{ weight: 500 }}>Cancel subscription</Text>
        <Box css={{ flex: 1 }} />
        <IconButton
          aria-label="Close"
          variant="plain"
          onClick={() =>
            navigate({
              to: "/purchases",
            })
          }
        >
          <CloseIcon />
        </IconButton>
      </Flex>
      <PageInset css={{ gap: 16, flexGrow: 1, pb: 16 }}>
        <Text css={{ fontScale: "2xl", fontWeight: 700 }}>Confirm cancellation</Text>
        <PurchaseTile purchase={subscription} />
        <Box css={{ p: 12, rounded: 8, backgroundColor: colors.yellow50 }}>
          {remaining.length > 0 ? (
            <Text css={{ fontScale: "sm" }}>
              This subscription has a minimum commitment.{" "}
              {remaining.length === 1 ? "1 more payment" : `${remaining.length} more payments`} of{" "}
              {subscription.token_amount} will still be taken from your wallet, on{" "}
              {joinWithAnd(remaining.map(d => formatDate(new Date(d))))}. Your subscription will then end on{" "}
              <strong>{formatDate(new Date(preview.endDate))}</strong>.
            </Text>
          ) : (
            <Text css={{ fontScale: "sm" }}>
              No further payments will be taken. Your subscription will end on{" "}
              <strong>{formatDate(new Date(preview.endDate))}</strong>.
            </Text>
          )}
        </Box>
        <Box css={{ flex: "1 0 0px" }} />
        <Button
          variant="filled"
          color="red"
          loading={mut.isPending}
          onClick={() => {
            mut.mutate(subscription.subscription_id, {
              onSuccess() {
                setCompleted(true);
              },
            });
          }}
        >
          Cancel subscription
        </Button>
      </PageInset>
      <SubscriptionDialog
        subscription={subscription}
        open={completed}
        onOpenChange={open => {
          setCompleted(open);
          if (!open) {
            setTimeout(() => {
              navigate({ to: "/purchases" });
            }, RECEIPT_DIALOG_EXIT_DURATION);
          }
        }}
      />
    </>
  );
}

function PurchaseTile({ purchase }: { purchase: SubscriptionDetails }) {
  return (
    <Flex
      css={{
        p: 10,
        border: `1px solid ${colors.outline}`,
        pos: "relative",
        rounded: 8,
        gap: 8,
      }}
    >
      <Box
        css={{
          w: 135,
          pos: "relative",
          aspectRatio: "1",
          overflow: "hidden",
          rounded: 4,
          cursor: "pointer",
        }}
      >
        <Img
          css={{
            pos: "absolute",
            top: 0,
            left: 0,
            w: "full",
            h: "full",
            objectFit: "cover",
          }}
          src={purchase.media_url ?? undefined}
        />
      </Box>
      <Flex css={{ flex: 1, flexDir: "column", justify: "space-between" }}>
        <div>
          <Text css={{ lineClamp: 2, fontScale: "sm", weight: 700 }}>{purchase.listing_title}</Text>
          <Text css={{ fontScale: "xs", weight: 500, mt: 4 }}>{purchase.variant_title}</Text>
        </div>
        <div>
          <Text css={{ fontScale: "2xs", weight: 700 }}>{splitTitleCase(purchase.supplier_name)}</Text>
          {purchase.next_payment_date && (
            <Flex css={{ gap: 4 }}>
              <CalendarIcon />
              <Text css={{ fontScale: "2xs", lineHeight: "12px" }}>
                {formatDate(new Date(purchase.next_payment_date))}
              </Text>
            </Flex>
          )}
        </div>
      </Flex>
    </Flex>
  );
}
